import { useState, useEffect } from "react";
import axios from "axios";
import {
	Box,
	Button,
	Tab,
	Tabs,
	TextField,
	Typography,
	Paper,
	List,
	ListItem,
	ListItemText,
	CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { MdDownload } from "react-icons/md";

// 🚨 If you use crypto/decryption logic for email, import from your utils if needed
// import { decryptData } from "../utils/crypto2";

const BRAND = {
	primary: "#7F070F",
	active: "#E3000F",
	hover: "#F68A8E",
	textOnPrimary: "#FFFFFF",
};

const ALTERNATE_TOKEN_KEY = "zenith_alternate_token";
const ENCRYPTED_EMAIL_KEY = "zenith_email";
const API_BASE_URL = "https://zenithsms.approot.ng/dashboard";

const Logs = () => {
	const [tab, setTab] = useState(0);
	const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);

	// === HANDLER: Download CSV for a given date ===
	const handleDownload = async () => {
		if (!date) {
			toast.error("Please select a date");
			return;
		}

		const token = localStorage.getItem(ALTERNATE_TOKEN_KEY);
		const encryptedEmail = localStorage.getItem(ENCRYPTED_EMAIL_KEY);
		const userEmail = encryptedEmail; // If encryption not applied, use directly

		if (!token || !userEmail) {
			toast.error("Authentication credentials missing. Please re-login.");
			return;
		}

		const authHeaders = {
			Authorization: `Bearer ${token}`,
			Email: userEmail,
		};

		toast.info("Preparing secure download link...");

		try {
			const response = await axios.get(
				`${API_BASE_URL}/create_download_link.php`,
				{
					params: { date },
					headers: authHeaders,
				}
			);

			if (response.data.status === "success" && response.data.downloadUrl) {
				window.open(response.data.downloadUrl, "_blank");
				toast.success("CSV download started.");
			} else {
				toast.error("Failed to generate secure link.");
			}
		} catch (error) {
			const msg =
				error.response?.data?.message ||
				"Download failed due to network or authentication error.";
			console.error("Link generation error:", error);
			toast.error(msg);
		}
	};

	// === HANDLER: Fetch log files from backend ===
	const fetchFiles = async () => {
		const token = localStorage.getItem(ALTERNATE_TOKEN_KEY);
		const email = localStorage.getItem(ENCRYPTED_EMAIL_KEY);

		if (!token || !email) {
			toast.error("Authentication credentials missing. Please re-login.");
			return;
		}

		setLoading(true);

		try {
			const res = await axios.get(`${API_BASE_URL}/list_logs.php`, {
				headers: {
					Authorization: `Bearer ${token}`,
					Email: email,
				},
			});

			if (res.data.status === "success") {
				setFiles(res.data.files);
			} else {
				toast.error(res.data.message || "Failed to fetch log files.");
			}
		} catch (err) {
			console.error(err);
			toast.error(err.response?.data?.message || "Error loading log list.");
		} finally {
			setLoading(false);
		}
	};

	// Auto-fetch files when tab changes to "File Download"
	useEffect(() => {
		if (tab === 1) fetchFiles();
	}, [tab]);

	return (
		<Box className='p-6' sx={{ color: BRAND.primary }}>
			<Typography variant='h5' fontWeight='bold' gutterBottom>
				🗂 Logs
			</Typography>

			<Paper elevation={3} sx={{ mt: 2 }}>
				<Tabs
					value={tab}
					onChange={(_, newValue) => setTab(newValue)}
					textColor='inherit'
					TabIndicatorProps={{ style: { backgroundColor: BRAND.active } }}
					sx={{
						"& .MuiTab-root": {
							color: BRAND.primary,
							fontWeight: "bold",
						},
						"& .Mui-selected": {
							color: BRAND.active,
						},
					}}
				>
					<Tab label='Daily Download' />
					<Tab label='File Download' />
				</Tabs>

				<Box sx={{ p: 3 }}>
					{/* ===================== TAB 0 ===================== */}
					{tab === 0 && (
						<Box>
							<Typography variant='subtitle1' gutterBottom>
								Download statistics CSV for a specific date
							</Typography>

							<form className='flex flex-col sm:flex-row items-center gap-4 mt-4'>
								<TextField
									label='Select Date'
									type='date'
									value={date}
									onChange={(e) => setDate(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
								<Button
									variant='contained'
									startIcon={<MdDownload />}
									onClick={handleDownload}
									sx={{
										bgcolor: BRAND.active,
										color: BRAND.textOnPrimary,
										fontWeight: "bold",
										minWidth: 160,
										"&:hover": {
											bgcolor: BRAND.hover,
										},
									}}
								>
									Download CSV
								</Button>
							</form>
						</Box>
					)}

					{/* ===================== TAB 1 ===================== */}
					{tab === 1 && (
						<Box>
							<Typography variant='subtitle1' gutterBottom>
								Available Log Archives
							</Typography>

							{loading ? (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										py: 5,
									}}
								>
									<CircularProgress />
								</Box>
							) : files.length > 0 ? (
								<List>
									{files.map((file) => (
										<ListItem
											key={file.name}
											divider
											secondaryAction={
												<Button
													variant='contained'
													startIcon={<MdDownload />}
													sx={{
														bgcolor: BRAND.active,
														color: BRAND.textOnPrimary,
														fontWeight: "bold",
														"&:hover": {
															bgcolor: BRAND.hover,
														},
													}}
													onClick={() =>
														window.open(file.downloadUrl, "_blank")
													}
												>
													Download
												</Button>
											}
										>
											<ListItemText
												primary={file.name}
												secondary={`Size: ${file.size_mb} MB — Modified: ${file.modified}`}
											/>
										</ListItem>
									))}
								</List>
							) : (
								<Typography>No log files found.</Typography>
							)}
						</Box>
					)}
				</Box>
			</Paper>
		</Box>
	);
};

export default Logs;
